import React, { useState } from "react";
import "@/App.css";
import { 
  Heart, 
  Smile, 
  Compass, 
  Sparkles, 
  BookOpen, 
  MessageCircle, 
  CheckCircle2, 
  ArrowRight, 
  Quote, 
  Menu, 
  X, 
  Award, 
  Calendar,
  ShieldCheck,
  Zap,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Toaster, toast } from "sonner";

// Constant Assets
const WHATSAPP_GROUP_URL = "https://chat.whatsapp.com/D8sTyb5knM5GYum9iyI0Af";
const MANJUSHA_IMAGE = "https://tagmango.com/assets/whatsapp-image-2026-10-03-at-10-41-05-am-ea411ef51a5703169c1bd49550bd07a0.jpeg";
const FAMILY_IMAGE = "https://tagmango.com/assets/505162940238914850071398026774020540339228220n-33cc5d1ac071a29a10595a22efde241e.jpg";
const LOGO_IMAGE = "https://tagmango.com/assets/whatsapp-image-2026-10-03-at-10-49-01-am-257f75cd16d2c50bb9f4bd06090c81d4.jpeg";

// Real personal parenting assets
const CHILD_HUG_1 = "https://tagmango.com/assets/whatsapp-image-2026-10-03-at-11-18-03-am-77cb8dfc7dff32cb479997260e8097e5.jpeg";
const CHILD_HUG_2 = "https://tagmango.com/assets/whatsapp-image-2026-10-03-at-11-18-03-am-1-d43c68f1dd486c5523f4bb63459d3dea.jpeg";

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMasterclassOpen, setIsMasterclassOpen] = useState(false);
  const [activeTipTab, setActiveTipTab] = useState(0);

  // Masterclass form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    childrenCount: "1",
    challenge: ""
  });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error("Please fill in your name and email address.");
      return;
    }
    
    // Simulate API registration call
    toast.success(`Congratulations ${formData.name}! Your free masterclass seat is reserved. Check your email shortly!`);
    setIsMasterclassOpen(false);
    setFormData({
      name: "",
      email: "",
      childrenCount: "1",
      challenge: ""
    });
  };

  const dailyTips = [
    {
      title: "Toddler Tantrums",
      scenario: "When your child is crying, screaming, and rolling on the floor",
      wrongWay: "Yelling 'Stop crying immediately or no screen time!' which increases cortisol and panic.",
      rightWay: "Get down to eye level, keep your voice low, and say: 'I see you are having a really hard time. I am right here with you.' This regulates their nervous system through yours.",
      actionable: "Connect before you correct. Physical proximity and calm breathing work faster than any verbal reasoning.",
      icon: Smile
    },
    {
      title: "Bedtime Battles",
      scenario: "When bedtime takes 2 hours and ends in mutual frustration",
      wrongWay: "Threatening 'Go to bed right now or I will throw away your toys!'",
      rightWay: "Establish a strict visual routine card and connect deeply for 10 minutes (whispering, massage, story) to ease separation anxiety.",
      actionable: "Fill their emotional cup right before sleep so they don't seek attention by resisting bedtime.",
      icon: Calendar
    },
    {
      title: "Defiance & 'No!'",
      scenario: "When you ask them to put on shoes and they flatly refuse",
      wrongWay: "Getting into a power struggle: 'Because I said so! Do it now!'",
      rightWay: "Offer controlled choices: 'Would you like to put on the red shoes or the blue shoes yourself, or should mommy help you?'",
      actionable: "Gives them a healthy sense of power and control, eliminating the need to show power through defiance.",
      icon: ShieldCheck
    },
    {
      title: "Sibling Conflicts",
      scenario: "When they are fighting and yelling over a toy",
      wrongWay: "Acting as the judge: 'Give it to your brother! You're older, you should know better!'",
      rightWay: "Act as a coach: 'You both want to play with the blocks. What can we do to solve this together so everyone is happy?'",
      actionable: "Builds long-term conflict resolution skills and prevents resentment between siblings.",
      icon: Users
    },
    {
      title: "Self-Regulation",
      scenario: "When you feel your anger rising and want to yell",
      wrongWay: "Letting the reactive brain take over, resulting in yelling and subsequent guilt.",
      rightWay: "Press pause. Say aloud: 'Mommy is feeling frustrated, I am going to take 3 deep breaths before I speak.'",
      actionable: "You are modeling emotional regulation. Children learn from what you DO, not what you say.",
      icon: Zap
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-[#2C3E35] flex flex-col selection:bg-[#3B6047]/20 selection:text-[#2C3E35] bg-grain font-sans antialiased">
      {/* Toast Notifications */}
      <Toaster position="top-center" richColors />

      {/* Sticky Header Navigation */}
      <header className="sticky top-0 z-40 w-full backdrop-blur-md bg-[#FAF8F5]/90 border-b border-[#3B6047]/10 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="rounded-full shadow-md overflow-hidden">
              <img 
                src={LOGO_IMAGE} 
                alt="Calm Parenting Hub Logo" 
                className="h-12 w-12 object-cover"
              />
            </div>
            <div>
              <span className="font-serif text-2xl font-bold tracking-tight text-[#2C3E35] block leading-none">
                Calm Parenting Hub
              </span>
              <span className="text-xs uppercase tracking-widest text-[#5A6B60] font-semibold">
                by Manjusha K.N.
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8 text-sm font-medium text-[#5A6B60]">
            <a href="#story" className="hover:text-[#3B6047] transition-colors duration-200">Our Story</a>
            <a href="#mission" className="hover:text-[#3B6047] transition-colors duration-200">Family Mission</a>
            <a href="#about" className="hover:text-[#3B6047] transition-colors duration-200">About Manjusha</a>
            <a href="#tips" className="hover:text-[#3B6047] transition-colors duration-200">Calming Tips</a>
            <a href="#faqs" className="hover:text-[#3B6047] transition-colors duration-200">FAQs</a>
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            <Button 
              data-testid="nav-whatsapp-btn"
              onClick={() => window.open(WHATSAPP_GROUP_URL, "_blank")}
              className="bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold rounded-full px-6 py-2 shadow-sm transition-all duration-300 flex items-center gap-2 hover:scale-105"
            >
              <MessageCircle className="h-4 w-4" />
              Join Group Free
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-[#2C3E35] hover:text-[#3B6047] transition-colors focus:outline-none"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation Drawer */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-20 left-0 w-full bg-[#FAF8F5] border-b border-[#3B6047]/10 shadow-lg px-4 pt-4 pb-6 space-y-4 flex flex-col">
            <a 
              href="#story" 
              onClick={() => setIsMenuOpen(false)}
              className="font-medium text-lg text-[#2C3E35] hover:text-[#3B6047] py-2 border-b border-[#3B6047]/5"
            >
              Our Story
            </a>
            <a 
              href="#mission" 
              onClick={() => setIsMenuOpen(false)}
              className="font-medium text-lg text-[#2C3E35] hover:text-[#3B6047] py-2 border-b border-[#3B6047]/5"
            >
              Family Mission
            </a>
            <a 
              href="#about" 
              onClick={() => setIsMenuOpen(false)}
              className="font-medium text-lg text-[#2C3E35] hover:text-[#3B6047] py-2 border-b border-[#3B6047]/5"
            >
              About Manjusha
            </a>
            <a 
              href="#tips" 
              onClick={() => setIsMenuOpen(false)}
              className="font-medium text-lg text-[#2C3E35] hover:text-[#3B6047] py-2 border-b border-[#3B6047]/5"
            >
              Calming Tips
            </a>
            <a 
              href="#faqs" 
              onClick={() => setIsMenuOpen(false)}
              className="font-medium text-lg text-[#2C3E35] hover:text-[#3B6047] py-2 border-b border-[#3B6047]/5"
            >
              FAQs
            </a>
            <div className="pt-2 flex flex-col gap-3">
              <Button 
                data-testid="nav-whatsapp-btn-mobile"
                onClick={() => {
                  window.open(WHATSAPP_GROUP_URL, "_blank");
                  setIsMenuOpen(false);
                }}
                className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold rounded-full py-3 shadow-md flex items-center justify-center gap-2"
              >
                <MessageCircle className="h-5 w-5" />
                Join WhatsApp Group
              </Button>
              <Button 
                onClick={() => {
                  setIsMasterclassOpen(true);
                  setIsMenuOpen(false);
                }}
                className="w-full bg-[#C87D55] hover:bg-[#B26943] text-white font-semibold rounded-full py-3 shadow-md"
              >
                Register for Free Masterclass
              </Button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-grow">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-12 sm:py-20 lg:py-28 bg-[#FAF8F5]" id="hero">
          {/* Subtle decorative background circles */}
          <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-[#3B6047]/5 blur-3xl pointer-events-none" />
          <div className="absolute bottom-10 -right-32 w-[40rem] h-[40rem] rounded-full bg-[#C87D55]/5 blur-3xl pointer-events-none" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              {/* Text Column */}
              <div className="lg:col-span-7 space-y-8 text-left">
                <div className="inline-flex items-center space-x-2 bg-[#E8F0EA] text-[#2D4A37] px-4 py-1.5 rounded-full text-xs sm:text-sm font-bold tracking-wider uppercase">
                  <Sparkles className="h-4 w-4" />
                  <span>Founder of Calm Parenting Hub</span>
                </div>

                <div className="space-y-4">
                  <p className="text-xs sm:text-sm tracking-[0.25em] font-extrabold text-[#C87D55] uppercase leading-none">
                    FROM FRUSTRATED TO CALM
                  </p>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-[#2C3E35] tracking-tight leading-[1.12]">
                    Helping parents create calmer, happier homes for their children.
                  </h1>
                </div>

                <p className="text-base sm:text-lg lg:text-xl text-[#5A6B60] leading-relaxed max-w-2xl">
                  Discover parenting and behavior coaching designed to dissolve daily power struggles. Stop relying on yelling, threats, or constant punishment—and guide your children to become future great leaders.
                </p>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                  <Button
                    data-testid="hero-whatsapp-cta"
                    onClick={() => window.open(WHATSAPP_GROUP_URL, "_blank")}
                    className="bg-[#25D366] hover:bg-[#128C7E] text-white text-base font-bold rounded-full px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 animate-pulse-soft"
                  >
                    <MessageCircle className="h-6 w-6" />
                    <span>Join Free WhatsApp Group</span>
                  </Button>
                  <Button
                    data-testid="hero-masterclass-cta"
                    onClick={() => setIsMasterclassOpen(true)}
                    className="bg-transparent hover:bg-[#3B6047]/5 text-[#3B6047] border-2 border-[#3B6047] text-base font-bold rounded-full px-8 py-6 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Award className="h-5 w-5" />
                    <span>Register for Free Masterclass</span>
                  </Button>
                </div>

                {/* Trust indicator */}
                <div className="pt-6 border-t border-[#3B6047]/10 flex flex-wrap items-center gap-6">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 rounded-full bg-[#3B6047] text-[#FAF8F5] flex items-center justify-center text-xs font-bold border-2 border-[#FAF8F5]">M1</div>
                    <div className="w-8 h-8 rounded-full bg-[#C87D55] text-[#FAF8F5] flex items-center justify-center text-xs font-bold border-2 border-[#FAF8F5]">M2</div>
                    <div className="w-8 h-8 rounded-full bg-[#2C3E35] text-[#FAF8F5] flex items-center justify-center text-xs font-bold border-2 border-[#FAF8F5]">M3</div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2C3E35]">Over 1,000+ Parents Guided</p>
                    <p className="text-xs text-[#5A6B60]">Join daily interactions and receive serene parenting guidance</p>
                  </div>
                </div>
              </div>

              {/* Image Frame Column */}
              <div className="lg:col-span-5 flex justify-center">
                <div className="relative w-full max-w-[400px] lg:max-w-none aspect-[4/5] sm:aspect-square lg:aspect-[4/5] rounded-[3rem] overflow-hidden shadow-2xl border-4 border-[#3B6047]/10">
                  <img 
                    src={MANJUSHA_IMAGE} 
                    alt="Manjusha K.N. - Parenting & Child Behaviour Coach" 
                    className="w-full h-full object-cover object-center"
                    loading="eager"
                  />
                  {/* Floating credentials overlay */}
                  <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm p-5 rounded-2xl shadow-xl border border-[#3B6047]/10">
                    <p className="font-serif text-lg font-bold text-[#2C3E35]">Manjusha K.N.</p>
                    <p className="text-xs text-[#5A6B60] font-semibold uppercase tracking-wider">Parenting & Child Behaviour Coach</p>
                    <div className="mt-2 flex items-center space-x-2 text-[#3B6047] text-xs font-bold">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>Certified Practitioner</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* OUR STORY SECTION */}
        <section className="py-16 sm:py-24 bg-[#F4EFEA] relative" id="story" data-testid="story-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
              <span className="text-xs tracking-[0.25em] font-extrabold text-[#C87D55] uppercase">THE JOURNEY</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-[#2C3E35] tracking-tight">
                The Woman Behind Calm Parenting Hub
              </h2>
              <p className="text-base sm:text-lg text-[#5A6B60]">
                "I wasn't always a calm parent. My coaching is built on real challenges, deep learning, and a total family transformation."
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              {/* Story timeline (Left 7 columns) */}
              <div className="lg:col-span-7 space-y-8 text-left">
                <div className="relative border-l-2 border-[#3B6047]/20 pl-6 sm:pl-8 space-y-12">
                  
                  {/* Step 1 */}
                  <div className="relative">
                    <span className="absolute -left-[35px] sm:-left-[43px] top-0.5 bg-[#3B6047] text-[#FAF8F5] rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm shadow">
                      1
                    </span>
                    <div className="space-y-2">
                      <h3 className="text-xl font-serif font-bold text-[#2C3E35]">The Naturally Calm Persona</h3>
                      <p className="text-[#5A6B60] text-sm sm:text-base">
                        Before starting my parenting journey, I was naturally calm and composed. I hold a Master's degree and was confident that my parenting journey would be flawless and filled with ease.
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="relative">
                    <span className="absolute -left-[35px] sm:-left-[43px] top-0.5 bg-[#C87D55] text-[#FAF8F5] rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm shadow">
                      2
                    </span>
                    <div className="space-y-2">
                      <h3 className="text-xl font-serif font-bold text-[#C87D55]">The Frustrated Parenting Reality</h3>
                      <p className="text-[#5A6B60] text-sm sm:text-base">
                        When my children arrived, the challenges hit hard. Sleep deprivation, toddler tantrums, and constant boundary-testing pushed me over the edge. I found myself feeling deeply frustrated and reactive—resorting to yelling and threats, followed by overwhelming parental guilt.
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="relative">
                    <span className="absolute -left-[35px] sm:-left-[43px] top-0.5 bg-[#3B6047] text-[#FAF8F5] rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm shadow">
                      3
                    </span>
                    <div className="space-y-2">
                      <h3 className="text-xl font-serif font-bold text-[#2C3E35]">The Turning Point & Hard Realities</h3>
                      <p className="text-[#5A6B60] text-sm sm:text-base">
                        I realized that my children were reacting to my stress. This wasn't working. I deep-dived into parenting science, child psychology, and behavioral coaching. I learned that calm is not a trait; it is a skill that must be trained.
                      </p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="relative">
                    <span className="absolute -left-[35px] sm:-left-[43px] top-0.5 bg-[#3B6047] text-[#FAF8F5] rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm shadow">
                      4
                    </span>
                    <div className="space-y-2">
                      <h3 className="text-xl font-serif font-bold text-[#2C3E35]">The Calm Family Transformation</h3>
                      <p className="text-[#5A6B60] text-sm sm:text-base">
                        By applying science-backed, positive behavior strategies, I transformed myself first, then my home. The yelling stopped. The cooperation increased. Our home became a peaceful, loving sanctuary.
                      </p>
                    </div>
                  </div>

                </div>
              </div>

              {/* Real parenting images (Right 5 columns) */}
              <div className="lg:col-span-5 space-y-6">
                <div className="relative rounded-[2rem] overflow-hidden shadow-xl aspect-square">
                  <img 
                    src={CHILD_HUG_1} 
                    alt="Manjusha KN with her child hugging" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2C3E35]/30 via-transparent to-transparent" />
                </div>
                <div className="relative rounded-[2rem] overflow-hidden shadow-xl aspect-square">
                  <img 
                    src={CHILD_HUG_2} 
                    alt="Manjusha KN child hugging and smiling" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2C3E35]/30 via-transparent to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6 text-white text-left">
                    <p className="font-serif text-lg italic">"Calmness is contagious. If we are not calm, we cannot teach them calm."</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* FAMILY IS THE HEART SECTION */}
        <section className="py-16 sm:py-24 bg-[#FAF8F5]" id="mission" data-testid="family-mission-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              
              {/* Family Image Column (Left 5 cols) */}
              <div className="lg:col-span-6">
                <div className="relative rounded-[3rem] overflow-hidden shadow-2xl border-4 border-white">
                  <img 
                    src={FAMILY_IMAGE} 
                    alt="Manjusha's Beautiful Family" 
                    className="w-full object-cover aspect-[4/3] sm:aspect-video lg:aspect-[4/3]"
                    loading="lazy"
                  />
                  <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-md">
                    <p className="text-xs font-bold text-[#3B6047] uppercase tracking-wider">The Heart of My Mission</p>
                  </div>
                </div>
              </div>

              {/* Mission Pillars (Right 7 cols) */}
              <div className="lg:col-span-6 space-y-8 text-left">
                <div className="space-y-4">
                  <span className="text-xs tracking-[0.25em] font-extrabold text-[#C87D55] uppercase">MY MISSION</span>
                  <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#2C3E35] tracking-tight">
                    My Family is the Heart of My Mission
                  </h2>
                </div>

                <div className="bg-[#F4EFEA] p-6 sm:p-8 rounded-2xl border border-[#3B6047]/10 relative">
                  <Quote className="absolute top-6 right-6 h-12 w-12 text-[#3B6047]/10" />
                  <p className="font-serif text-lg sm:text-xl text-[#2C3E35] italic leading-relaxed">
                    "Every child deserves a calm home, and every parent deserves the confidence to lead with love."
                  </p>
                  <p className="mt-4 text-xs font-bold uppercase tracking-wider text-[#5A6B60]">— Manjusha K.N.</p>
                </div>

                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="bg-[#E8F0EA] text-[#2D4A37] h-10 w-10 sm:h-12 sm:w-12 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                      <Heart className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-[#2C3E35]">Emotional Safety First</h4>
                      <p className="text-sm sm:text-base text-[#5A6B60]">
                        Creating a secure home harbor where kids are free to feel, fail, and express big emotions without being shut down.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="bg-[#E8F0EA] text-[#2D4A37] h-10 w-10 sm:h-12 sm:w-12 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                      <Compass className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-[#2C3E35]">Raising Future Leaders</h4>
                      <p className="text-sm sm:text-base text-[#5A6B60]">
                        Focusing on teaching children emotional self-regulation, empathy, and resilience, prepping them to be incredible future leaders.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="bg-[#E8F0EA] text-[#2D4A37] h-10 w-10 sm:h-12 sm:w-12 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                      <Smile className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-[#2C3E35]">Connection Over Control</h4>
                      <p className="text-sm sm:text-base text-[#5A6B60]">
                        Replacing demanding obedience with building solid, lifelong connections, making cooperation naturally flow.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ABOUT ME SECTION */}
        <section className="py-16 sm:py-24 bg-[#F4EFEA] relative" id="about">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto bg-white rounded-[3rem] shadow-xl overflow-hidden border border-[#3B6047]/10" data-testid="about-me-card">
              <div className="grid grid-cols-1 md:grid-cols-12 items-center">
                
                {/* Coach portrait */}
                <div className="md:col-span-5 h-full min-h-[350px] relative">
                  <img 
                    src={MANJUSHA_IMAGE} 
                    alt="Manjusha K.N." 
                    className="w-full h-full object-cover object-center absolute inset-0"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-white/10" />
                </div>

                {/* Profile text */}
                <div className="md:col-span-7 p-8 sm:p-12 text-left space-y-6">
                  <div className="space-y-2">
                    <span className="text-xs tracking-[0.25em] font-extrabold text-[#C87D55] uppercase">MEET YOUR COACH</span>
                    <h3 className="text-3xl sm:text-4xl font-serif font-bold text-[#2C3E35]">Manjusha K.N.</h3>
                    <p className="text-sm uppercase tracking-widest text-[#3B6047] font-semibold">
                      Parenting & Child Behaviour Coach • Founder, Calm Parenting Hub
                    </p>
                  </div>

                  <p className="text-[#5A6B60] text-sm sm:text-base leading-relaxed">
                    Hello! I'm Manjusha, a dedicated parent coach passionate about supporting parents raising children. Through my masterclasses, active WhatsApp community, and daily tips, I guide parents from states of daily power struggles to absolute connection and calm parenting.
                  </p>

                  {/* Badges and Metrics */}
                  <div className="grid grid-cols-3 gap-4 py-4 border-t border-b border-[#3B6047]/10">
                    <div>
                      <p className="text-2xl sm:text-3xl font-serif font-bold text-[#3B6047]">1K+</p>
                      <p className="text-xs text-[#5A6B60] uppercase tracking-wider font-semibold">Parents Mentored</p>
                    </div>
                    <div>
                      <p className="text-2xl sm:text-3xl font-serif font-bold text-[#3B6047]">10K+</p>
                      <p className="text-xs text-[#5A6B60] uppercase tracking-wider font-semibold">Tips Dispatched</p>
                    </div>
                    <div>
                      <p className="text-2xl sm:text-3xl font-serif font-bold text-[#3B6047]">5+ Yrs</p>
                      <p className="text-xs text-[#5A6B60] uppercase tracking-wider font-semibold">Coaching Exp</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
                    <Button
                      onClick={() => window.open(WHATSAPP_GROUP_URL, "_blank")}
                      className="w-full sm:w-auto bg-[#25D366] hover:bg-[#128C7E] text-white font-bold rounded-full px-8 py-5 transition-all flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="h-5 w-5" />
                      Get Daily Tips
                    </Button>
                    <Button
                      onClick={() => setIsMasterclassOpen(true)}
                      className="w-full sm:w-auto bg-[#C87D55] hover:bg-[#B26943] text-white font-bold rounded-full px-8 py-5 transition-all"
                    >
                      Join Free Masterclass
                    </Button>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* DAILY TIPS & INTERACTIVE STRATEGIES */}
        <section className="py-16 sm:py-24 bg-[#FAF8F5] relative" id="tips" data-testid="daily-tips-carousel">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
              <span className="text-xs tracking-[0.25em] font-extrabold text-[#C87D55] uppercase">DAILY CALMING PROTOCOL</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-[#2C3E35] tracking-tight">
                Daily Tips to Stay Calm & Build Cooperation
              </h2>
              <p className="text-base sm:text-lg text-[#5A6B60]">
                Click the categories below to discover simple, daily behavior-shift protocols you can practice with your kids starting today.
              </p>
            </div>

            {/* Interactive Tab Interface */}
            <div className="max-w-5xl mx-auto space-y-8">
              {/* Tab headers */}
              <div className="flex flex-wrap justify-center gap-2 border-b border-[#3B6047]/10 pb-4">
                {dailyTips.map((tip, index) => {
                  const Icon = tip.icon;
                  return (
                    <button
                      key={index}
                      onClick={() => setActiveTipTab(index)}
                      className={`flex items-center gap-2 px-4 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                        activeTipTab === index
                          ? "bg-[#3B6047] text-[#FAF8F5] shadow-md scale-105"
                          : "bg-white text-[#5A6B60] hover:bg-[#FAF8F5] hover:text-[#3B6047] border border-[#3B6047]/10"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tip.title}</span>
                    </button>
                  );
                })}
              </div>

              {/* Tab contents panel */}
              <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-xl border border-[#3B6047]/10 text-left transition-all duration-500 transform scale-100">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  
                  <div className="lg:col-span-8 space-y-6">
                    <div className="space-y-2">
                      <span className="text-xs uppercase tracking-wider text-[#C87D55] font-extrabold">Active Behavior Tip</span>
                      <h3 className="text-2xl sm:text-3xl font-serif font-bold text-[#2C3E35]">
                        The {dailyTips[activeTipTab].title} Blueprint
                      </h3>
                      <p className="text-[#2C3E35] font-medium bg-[#F4EFEA] px-4 py-2.5 rounded-lg border-l-4 border-[#C87D55] text-sm sm:text-base">
                        <strong>Scenario:</strong> {dailyTips[activeTipTab].scenario}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="bg-red-50 p-5 rounded-xl border border-red-100">
                        <p className="text-xs font-bold text-red-700 uppercase tracking-widest mb-2">❌ What NOT to do</p>
                        <p className="text-sm text-red-900 leading-relaxed">{dailyTips[activeTipTab].wrongWay}</p>
                      </div>
                      <div className="bg-[#E8F0EA] p-5 rounded-xl border border-emerald-100">
                        <p className="text-xs font-bold text-[#2D4A37] uppercase tracking-widest mb-2">✅ The Calm Way</p>
                        <p className="text-sm text-[#1E3325] leading-relaxed">{dailyTips[activeTipTab].rightWay}</p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-[#3B6047]/10 space-y-2">
                      <p className="text-xs font-bold uppercase tracking-wider text-[#5A6B60]">Actionable Exercise</p>
                      <p className="text-base font-semibold text-[#2C3E35]">{dailyTips[activeTipTab].actionable}</p>
                    </div>
                  </div>

                  <div className="lg:col-span-4 bg-[#F4EFEA] p-6 rounded-2xl border border-[#3B6047]/10 text-center space-y-6">
                    <Sparkles className="h-10 w-10 text-[#C87D55] mx-auto" />
                    <h4 className="text-lg font-bold text-[#2C3E35]">Want the complete Parenting Toolkit?</h4>
                    <p className="text-sm text-[#5A6B60]">
                      Get detailed, daily worksheets and stay calm behavior strategies. Join over 1,000+ parents in our WhatsApp Group today.
                    </p>
                    <Button
                      onClick={() => window.open(WHATSAPP_GROUP_URL, "_blank")}
                      className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-bold rounded-full py-3 flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="h-5 w-5" />
                      Join Free Group
                    </Button>
                  </div>

                </div>
              </div>

              {/* Masterclass Highlight Banner */}
              <div className="bg-gradient-to-br from-[#3B6047] to-[#1E3325] text-white rounded-3xl p-8 sm:p-12 shadow-2xl relative overflow-hidden text-left">
                {/* Decorative circle */}
                <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-white/5" />
                <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                  <div className="lg:col-span-8 space-y-4">
                    <div className="inline-flex items-center space-x-2 bg-white/20 text-[#FAF8F5] px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-widest">
                      <Sparkles className="h-3.5 w-3.5" />
                      <span>Next Masterclass Approaching</span>
                    </div>
                    <h3 className="text-3xl sm:text-4xl font-serif font-bold tracking-tight">
                      Calm Parenting Hub Masterclass
                    </h3>
                    <p className="text-[#E8F0EA] text-base sm:text-lg max-w-2xl">
                      Learn the 3-step positive coaching blueprint to dissolve shouting, stop daily pushbacks, and foster absolute connection with your children.
                    </p>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 pt-2 text-sm text-[#E8F0EA] font-medium">
                      <span className="flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4 text-[#C87D55]" />
                        90 Minutes E2E Guide
                      </span>
                      <span className="flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4 text-[#C87D55]" />
                        Completely Free Seat
                      </span>
                      <span className="flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4 text-[#C87D55]" />
                        Interactive Q&A Session
                      </span>
                    </div>
                  </div>
                  <div className="lg:col-span-4 flex justify-end">
                    <Button
                      data-testid="masterclass-trigger-btn"
                      onClick={() => setIsMasterclassOpen(true)}
                      className="w-full lg:w-auto bg-[#C87D55] hover:bg-[#B26943] text-white text-base font-bold rounded-full px-8 py-6 shadow-lg transition-transform duration-200 hover:scale-105"
                    >
                      Reserve My Free Seat
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* HIGH CONVERTING FAQS */}
        <section className="py-16 sm:py-24 bg-[#F4EFEA] relative" id="faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
              <span className="text-xs tracking-[0.25em] font-extrabold text-[#C87D55] uppercase">PARENTING OBJECTIONS</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-[#2C3E35] tracking-tight">
                Frequently Asked Questions
              </h2>
              <p className="text-base sm:text-lg text-[#5A6B60]">
                Got questions or feeling skeptical? Let's address your concerns straightforwardly.
              </p>
            </div>

            <div className="max-w-3xl mx-auto bg-white p-6 sm:p-10 rounded-3xl shadow-xl border border-[#3B6047]/10 text-left">
              <Accordion type="single" collapsible className="w-full space-y-4" data-testid="faq-accordion">
                
                <AccordionItem value="item-1" className="border-b border-[#3B6047]/10">
                  <AccordionTrigger className="text-base sm:text-lg font-serif font-bold text-[#2C3E35] hover:text-[#3B6047]">
                    What if my child is extremely strong-willed or stubborn?
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-[#5A6B60] leading-relaxed">
                    Strong-willed children aren't "bad"—they are born leaders who are highly sensitive to control. Our positive parenting methods teach you how to give them healthy power and autonomy, which automatically ends the defiance.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2" className="border-b border-[#3B6047]/10">
                  <AccordionTrigger className="text-base sm:text-lg font-serif font-bold text-[#2C3E35] hover:text-[#3B6047]">
                    How much time do I need daily to see results?
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-[#5A6B60] leading-relaxed">
                    You don't need hours. The daily calming routines take only 10–15 minutes of structured connection and small phrasing shifts during triggers. Most parents observe a massive shift in their child's cooperation within 7–10 days.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3" className="border-b border-[#3B6047]/10">
                  <AccordionTrigger className="text-base sm:text-lg font-serif font-bold text-[#2C3E35] hover:text-[#3B6047]">
                    Is the WhatsApp community completely free?
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-[#5A6B60] leading-relaxed">
                    Yes, joining the WhatsApp group is 100% free. It is my dedicated space to support parenting with daily tips, quick interactions, and masterclass updates.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4" className="border-b border-[#3B6047]/10">
                  <AccordionTrigger className="text-base sm:text-lg font-serif font-bold text-[#2C3E35] hover:text-[#3B6047]">
                    I've tried positive parenting before and it failed. Why is this different?
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-[#5A6B60] leading-relaxed">
                    Many materials describe "gentle parenting" as being permissive and letting children do anything. Our curriculum emphasizes child behavior coaching—which balances high warmth with firm, loving boundaries.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5" className="border-b border-[#3B6047]/10">
                  <AccordionTrigger className="text-base sm:text-lg font-serif font-bold text-[#2C3E35] hover:text-[#3B6047]">
                    Is this program only for mothers?
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-[#5A6B60] leading-relaxed">
                    No! While many of our members are mothers, we warmly welcome fathers, grandparents, teachers, and any primary guardian seeking to raise emotionally healthy children with confidence.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-6" className="border-none">
                  <AccordionTrigger className="text-base sm:text-lg font-serif font-bold text-[#2C3E35] hover:text-[#3B6047]">
                    What is the schedule of the masterclass?
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-[#5A6B60] leading-relaxed">
                    Our free masterclass occurs weekly. Once you register using our signup modal, we will immediately send you a link to choose your preferred time slot and receive the masterclass access credentials.
                  </AccordionContent>
                </AccordionItem>

              </Accordion>
            </div>
          </div>
        </section>

        {/* FINAL HIGH-IMPACT CTA */}
        <section className="py-16 sm:py-24 bg-white relative overflow-hidden" id="final-cta">
          {/* Accent decoration */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#FAF8F5]/80 pointer-events-none" />

          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="bg-[#FAF8F5] rounded-[3.5rem] p-8 sm:p-16 border border-[#3B6047]/15 shadow-2xl text-center space-y-8">
              
              {/* Photo + Quote grouping */}
              <div className="flex flex-col items-center space-y-4">
                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-[#3B6047]/20 shadow-md">
                  <img 
                    src={MANJUSHA_IMAGE} 
                    alt="Manjusha K.N." 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="space-y-1">
                  <p className="font-serif text-2xl sm:text-3xl font-bold text-[#2C3E35]">Your calmer home can begin today.</p>
                  <p className="text-sm text-[#5A6B60] max-w-xl mx-auto">
                    Stop letting yelling deplete your family's joy. Take the first step and let me support you on this journey.
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-center justify-center space-y-4 max-w-md mx-auto">
                <Button
                  data-testid="final-whatsapp-cta"
                  onClick={() => window.open(WHATSAPP_GROUP_URL, "_blank")}
                  className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white text-lg font-bold rounded-full py-6 shadow-xl flex items-center justify-center gap-3 hover:scale-105 transition-transform animate-pulse-soft"
                >
                  <MessageCircle className="h-6 w-6" />
                  <span>Join WhatsApp Community</span>
                </Button>
                
                <p className="text-xs text-[#5A6B60] font-medium uppercase tracking-wider">
                  ⚡ INSTANT FREE ACCESS TO DAILY TIPS & EXCLUSIVE ALERTS
                </p>
              </div>

            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="bg-[#1E3325] text-[#E8F0EA] py-12 border-t border-white/5 text-left">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center pb-8 border-b border-white/10">
            
            <div className="md:col-span-5 space-y-3">
              <div className="flex items-center space-x-3">
                <img 
                  src={LOGO_IMAGE} 
                  alt="Calm Parenting Hub Logo" 
                  className="h-10 w-10 rounded-full object-cover border border-white/10"
                />
                <span className="font-serif text-2xl font-bold tracking-tight text-[#FAF8F5]">
                  Calm Parenting Hub
                </span>
              </div>
              <p className="text-sm text-[#A3B899] max-w-sm">
                Empowering parents to coach, guide, and raise emotionally healthy children with positive, connection-led tools.
              </p>
            </div>

            <div className="md:col-span-4 space-y-2">
              <p className="text-xs font-bold uppercase tracking-widest text-[#FAF8F5]">Quick Connections</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm font-medium">
                <a href="#story" className="hover:text-white transition-colors">Our Story</a>
                <a href="#mission" className="hover:text-white transition-colors">Mission</a>
                <a href="#about" className="hover:text-white transition-colors">Coach Profile</a>
                <a href="#tips" className="hover:text-white transition-colors">Daily Tips</a>
                <a href="#faqs" className="hover:text-white transition-colors">FAQs</a>
              </div>
            </div>

            <div className="md:col-span-3 text-left md:text-right">
              <Button
                onClick={() => window.open(WHATSAPP_GROUP_URL, "_blank")}
                className="bg-white/10 hover:bg-white/20 text-[#FAF8F5] border border-white/10 font-bold rounded-full px-6 py-2.5 transition-all text-xs"
              >
                Join Free Community
              </Button>
            </div>

          </div>

          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-[#A3B899] gap-4">
            <div>
              <p>© {new Date().getFullYear()} Calm Parenting Hub. All rights reserved.</p>
              <p className="mt-1">
                Parenting coaching is educational and supportive; it is not a substitute for clinical child therapy or medical diagnosis.
              </p>
            </div>
            <div className="flex gap-4">
              <span className="text-[#A3B899]">Built by Mango AI inside TagMango Studio</span>
              <a href="https://learn.manjushakn.com" className="hover:text-white underline">learn.manjushakn.com</a>
            </div>
          </div>
        </div>
      </footer>

      {/* MASTERCLASS SIGNUP MODAL DIALOG */}
      <Dialog open={isMasterclassOpen} onOpenChange={setIsMasterclassOpen}>
        <DialogContent className="sm:rounded-3xl border-[#3B6047]/15 p-6 sm:p-10 bg-[#FAF8F5] text-left max-w-lg shadow-2xl">
          <DialogHeader className="space-y-3">
            <div className="inline-flex items-center space-x-2 bg-[#E8F0EA] text-[#2D4A37] px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-widest w-fit">
              <Award className="h-3.5 w-3.5" />
              <span>Free Seat Registration</span>
            </div>
            <DialogTitle className="font-serif text-2xl sm:text-3xl font-bold text-[#2C3E35] tracking-tight">
              Join the Calm Parenting Masterclass
            </DialogTitle>
            <DialogDescription className="text-sm text-[#5A6B60]">
              Unlock positive parenting tools for your children. Please fill out your details to receive access instructions.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleFormSubmit} className="space-y-6 pt-4">
            <div className="space-y-1">
              <label htmlFor="name" className="text-sm font-bold text-[#2C3E35] block">Parent's Full Name *</label>
              <input 
                type="text" 
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleFormChange}
                placeholder="e.g. Priya Sharma"
                className="w-full bg-white border border-[#3B6047]/20 rounded-xl px-4 py-3.5 text-sm sm:text-base text-[#2C3E35] focus:outline-none focus:ring-2 focus:ring-[#3B6047] placeholder:text-[#5A6B60]/40 transition-all shadow-sm"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="email" className="text-sm font-bold text-[#2C3E35] block">Email Address *</label>
              <input 
                type="email" 
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleFormChange}
                placeholder="e.g. priya@gmail.com"
                className="w-full bg-white border border-[#3B6047]/20 rounded-xl px-4 py-3.5 text-sm sm:text-base text-[#2C3E35] focus:outline-none focus:ring-2 focus:ring-[#3B6047] placeholder:text-[#5A6B60]/40 transition-all shadow-sm"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label htmlFor="childrenAgeGroup" className="text-sm font-bold text-[#2C3E35] block">Children's Age Range</label>
                <select 
                  id="childrenAgeGroup"
                  name="childrenCount"
                  value={formData.childrenCount}
                  onChange={handleFormChange}
                  className="w-full bg-white border border-[#3B6047]/20 rounded-xl px-4 py-3.5 text-sm sm:text-base text-[#2C3E35] focus:outline-none focus:ring-2 focus:ring-[#3B6047] transition-all shadow-sm cursor-pointer"
                >
                  <option value="toddler">Toddler (1-3 yrs)</option>
                  <option value="preschooler">Preschooler (3-5 yrs)</option>
                  <option value="school-age">School Age (6-12 yrs)</option>
                  <option value="teenager">Teenager (13+ yrs)</option>
                </select>
              </div>

              <div className="space-y-1">
                <label htmlFor="challenge" className="text-sm font-bold text-[#2C3E35] block">Biggest Challenge</label>
                <select 
                  id="challenge"
                  name="challenge"
                  value={formData.challenge}
                  onChange={handleFormChange}
                  className="w-full bg-white border border-[#3B6047]/20 rounded-xl px-4 py-3.5 text-sm sm:text-base text-[#2C3E35] focus:outline-none focus:ring-2 focus:ring-[#3B6047] transition-all shadow-sm cursor-pointer"
                >
                  <option value="">Select your main issue...</option>
                  <option value="yelling">Yelling & Angry Outbursts</option>
                  <option value="stubborn">Stubbornness & Refusing</option>
                  <option value="tantrums">Toddler Tantrums</option>
                  <option value="bedtime">Bedtime & Routine Struggles</option>
                  <option value="sibling">Sibling Fighting & Jealousy</option>
                </select>
              </div>
            </div>

            <div className="pt-2 flex items-center justify-between gap-4">
              <Button
                type="button"
                onClick={() => setIsMasterclassOpen(false)}
                className="bg-transparent hover:bg-[#3B6047]/5 text-[#5A6B60] font-bold rounded-full py-4.5 px-6 transition-all"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#C87D55] hover:bg-[#B26943] text-white font-bold rounded-full py-4.5 px-8 shadow-md transition-all flex items-center gap-2 hover:scale-102 shrink-0"
              >
                <span>Reserve Seat Free</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;
